import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import { CatButton, CatModal, CatHeading } from 'components/blocks-components';
import { replaceTokensInString } from '@cat-ecom/common/utils/stringUtils';
import { CONTACT_US_DEALER } from 'constants/endpoints';
import { useEsiteCtx } from 'services/esite/context';
import { normalizeUrl } from 'utils/url';
import { getSeoUrl } from 'utils/links';
import styles from './my-equipment-modal.module.scss';

type ValidateAssetModalType = {
  isActive: boolean;
  closeEditModal: () => void;
};

const ValidateAssetModal = ({
  isActive,
  closeEditModal
}: Readonly<ValidateAssetModalType>) => {
  const t = useTranslations();
  // TODO - Input Validation - Ensure that all values used from params are allowed values:
  // lang should always be a two-digit string without numbers or special characters (like 'es');
  // esite should always be a string without numbers or special characters, except for the dash (like demo-dealer).
  const params = useParams();
  const { catalogId, langId, storeId } = useEsiteCtx();
  const esiteUrl = getSeoUrl(params);

  const returnContactUrl = useMemo(
    () =>
      normalizeUrl(
        replaceTokensInString(
          `${esiteUrl}${CONTACT_US_DEALER}`,
          catalogId,
          langId,
          storeId
        )
      ),
    [catalogId, esiteUrl, langId, storeId]
  );

  const handleRedirectRoute = () => {
    window.open(returnContactUrl, '_self');
    closeEditModal();
  };
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    closeEditModal();
  };

  return (
    <CatModal
      isActive={isActive}
      onClick={(e) => handleClick(e)}
      data-testid="validate-asset-Modal"
      size="sm"
      width="448px"
      className={styles['validated-asset-modal']}
      returnFocusOnDeactivate={false}
    >
      <CatHeading
        slot="header"
        variant={'title-sm'}
        data-testid="validate-asset-heading"
      >
        {t('MEQ_EDIT_EQUIP')}
      </CatHeading>
      <div data-testid="validate-asset-description" className="mb-2">
        {t('MEQ_DEALER_MANAGED')}
      </div>

      <div slot="footer" className="pt-0 cat-u-display-flex gap-2">
        <CatButton onClick={handleRedirectRoute} data-testid="redirect-button">
          {t('CONTACT_YOUR_DEALER')}
        </CatButton>
        <CatButton
          data-testid="cancel-button"
          variant="ghost"
          onClick={closeEditModal}
        >
          <span>{t('CANCEL')}</span>
        </CatButton>
      </div>
    </CatModal>
  );
};

export default ValidateAssetModal;
